/**
 * Interaction events for the Sage pixel.
 *
 * Shopify's own `clicked` customer event names an element by tag, id, href and
 * name only, so a pixel can tell that *a* link was clicked but not which one:
 * no label, no component, no section. This script publishes what the pixel
 * cannot see for itself, as Shopify customer events (custom events reach
 * custom pixels only — the Sage pixel subscribes to them, sage-forcast
 * pixel/src/payload.ts):
 *
 *   forcast:click         every click on an interactive element: a link, a
 *                         button, anything with role="button", a submit input,
 *                         a <summary>, an element with a declarative on:click,
 *                         or anything carrying data-track.
 *   forcast:change        every change to a control that holds a choice — a
 *                         select, checkbox, radio, range or number input. Text
 *                         fields are never read: what is typed into them may be
 *                         personal, and Shopify's own input_* events already
 *                         say which field was used.
 *   forcast:section_view  each .shopify-section the shopper reaches, once per
 *                         page (Theme settings → Analytics; off by default,
 *                         because it adds several events per page view).
 *
 * WHAT A CLICK CARRIES
 *   el         a structural key for the element: data-track if set, else
 *              <component or area>.<data-testid | ref | name | id | first class | tag>
 *              e.g. product-card.productCardLink, header.forcast-nav__link,
 *              add-to-cart-component.button. Generated ids (hashes, block ids)
 *              are skipped so the key is the same on every page.
 *   text       the visible label (or aria-label / alt / title), 80 characters —
 *              "Dresses", "Add to bag", "Open cart". Static copy, never a value
 *              a shopper typed.
 *   area       header | footer | dialog | main
 *   section    the section's name from its Shopify wrapper id ("main",
 *              "header", "featured_collection"), random suffix stripped
 *   component  the nearest custom element (product-card, variant-picker, …)
 *   href       for links: the path (same origin) or host + path (external)
 *   productId  from the nearest [data-product-id], variantId likewise
 *   pos        1-based position of the product card in its list
 *
 * A control change carries the same context plus the control's name, type,
 * value and checked state. A section view carries the section, its area, its
 * position down the page, and whether it was on screen at load or scrolled to.
 *
 * Volume is bounded per page (see the MAX_* constants) and a double-tap on the
 * same element within 400 ms counts once. `data-track-ignore` on an element or
 * any ancestor switches the whole subtree off. Nothing here can throw into the
 * page: every listener is wrapped, and a missing Shopify.analytics is retried
 * briefly and then given up on.
 *
 * Loaded from snippets/scripts.liquid as a classic deferred script (not a
 * module), so the Liquid settings can ride on the tag's data attributes.
 */
(() => {
  if (window.forcastInteractions) return;

  const script = document.currentScript;
  const SECTION_VIEWS = script?.dataset.sectionViews === 'true';

  const MAX_CLICKS = 100;
  const MAX_CHANGES = 60;
  const MAX_SECTIONS = 40;
  const DOUBLE_TAP_MS = 400;
  const TEXT_MAX = 80;
  const HREF_MAX = 512;

  const INTERACTIVE =
    'a[href], button, [role="button"], input[type="submit"], input[type="button"], summary, [on\\:click], [data-track]';
  /** Controls whose value is a choice, not something typed. */
  const CHOICE_TYPES = new Set(['select', 'checkbox', 'radio', 'range', 'number']);

  /* ---- publishing ------------------------------------------------------ */

  /** Events that arrived before Shopify.analytics did; replayed for a few seconds, then dropped. */
  const pending = [];
  let retries = 0;

  const analytics = () => {
    const publish = window.Shopify?.analytics?.publish;
    return typeof publish === 'function' ? publish.bind(window.Shopify.analytics) : null;
  };

  const flushPending = () => {
    const publish = analytics();
    if (publish) {
      for (const [name, data] of pending.splice(0)) {
        try {
          publish(name, data);
        } catch {
          /* best effort */
        }
      }
      return;
    }
    if (retries++ < 20) setTimeout(flushPending, 500);
    else pending.length = 0;
  };

  const publish = (name, data) => {
    try {
      const fn = analytics();
      if (fn) {
        fn(name, data);
        return;
      }
      if (pending.length < 30) pending.push([name, data]);
      if (pending.length === 1) setTimeout(flushPending, 500);
    } catch {
      /* analytics is best effort; it must never stop a click from working */
    }
  };

  /* ---- describing elements --------------------------------------------- */

  const collapse = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();
  /* Case is kept so a key reads like the markup it came from (productCardLink, not productcardlink). */
  const slug = (s) =>
    collapse(s)
      .replace(/[^A-Za-z0-9_:.-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
  /* Horizon suffixes option inputs with the section id ("Size-template--…"): one name per option. */
  const controlName = (el) => (el.getAttribute('name') || '').replace(/-(template|sections)--.*$/, '');

  /** A random suffix as Shopify mints them for blocks and sections: "_9WaLn7", "_Hhh3k8". */
  const RANDOM_SUFFIX = /_(?=[A-Za-z0-9]*\d)[A-Za-z0-9]{6}$/;
  /** md5 ids, numeric Shopify ids and template/section ids change per render or per page. */
  const looksGenerated = (id) =>
    /[0-9a-f]{16,}/i.test(id) || /\d{5,}/.test(id) || /^(template|sections)--/.test(id) || RANDOM_SUFFIX.test(id);

  const customElementOf = (el) => {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      if (node.tagName.includes('-')) return node.tagName.toLowerCase();
    }
    return null;
  };

  const areaOf = (el) => {
    if (el.closest('dialog, [role="dialog"], [aria-modal="true"]')) return 'dialog';
    if (el.closest('#header-group, header-component, .forcast-header')) return 'header';
    if (el.closest('footer, .forcast-footer-section')) return 'footer';
    return 'main';
  };

  /** "shopify-section-template--123__featured_collection_Hhh3k8" → "featured_collection". */
  const sectionNameOf = (wrapper) => {
    if (!wrapper) return null;
    const id = wrapper.id.replace(/^shopify-section-/, '');
    const tail = id.includes('__') ? id.slice(id.lastIndexOf('__') + 2) : id;
    return slug(tail.replace(RANDOM_SUFFIX, '')) || null;
  };
  const sectionOf = (el) => sectionNameOf(el.closest('.shopify-section'));

  const labelOf = (el) => {
    const aria = el.getAttribute('aria-label');
    if (aria) return collapse(aria).slice(0, TEXT_MAX);
    /* A submit/button input's value is its label; any other input's value is what the shopper typed. */
    if (el instanceof HTMLInputElement) {
      return el.type === 'submit' || el.type === 'button' || el.type === 'reset'
        ? collapse(el.value).slice(0, TEXT_MAX) || null
        : null;
    }
    const text = collapse(el.textContent);
    if (text) return text.slice(0, TEXT_MAX);
    const img = el.querySelector('img[alt]');
    if (img?.alt) return collapse(img.alt).slice(0, TEXT_MAX);
    const title = el.getAttribute('title');
    return title ? collapse(title).slice(0, TEXT_MAX) : null;
  };

  const roleOf = (el) => {
    const testId = el.getAttribute('data-testid');
    if (testId) return slug(testId);
    const ref = el.getAttribute('ref');
    if (ref) return slug(ref);
    const name = controlName(el);
    if (name) return slug(name);
    if (el.id && !looksGenerated(el.id)) return slug(el.id);
    const cls = el.classList[0];
    if (cls && !looksGenerated(cls)) return slug(cls);
    return el.tagName.toLowerCase();
  };

  const keyOf = (el) => {
    const tracked = el.closest('[data-track]');
    if (tracked?.dataset.track) return slug(tracked.dataset.track);
    return `${customElementOf(el) ?? areaOf(el)}.${roleOf(el)}`;
  };

  const hrefOf = (el) => {
    if (!(el instanceof HTMLAnchorElement) || !el.href) return null;
    try {
      const url = new URL(el.href, location.href);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return url.protocol;
      const path = `${url.pathname}${url.search}`;
      return (url.origin === location.origin ? path : `${url.host}${path}`).slice(0, HREF_MAX);
    } catch {
      return null;
    }
  };

  const productOf = (el) => {
    const card = el.closest('[data-product-id]');
    const variant =
      el.closest('[data-variant-id]')?.dataset.variantId ??
      el.closest('[data-quick-add-variant-id]')?.dataset.quickAddVariantId ??
      null;
    return { productId: card?.dataset.productId || null, variantId: variant || null, card };
  };

  /** 1-based index of the card in its list: the ancestor that has siblings is the list item. */
  const positionOf = (card) => {
    if (!card) return null;
    let item = card;
    while (item.parentElement && item.parentElement !== document.body && item.parentElement.children.length === 1) {
      item = item.parentElement;
    }
    const parent = item.parentElement;
    if (!parent || parent === document.body) return null;
    return Array.prototype.indexOf.call(parent.children, item) + 1;
  };

  const contextOf = (el) => {
    const { productId, variantId, card } = productOf(el);
    return {
      el: keyOf(el),
      area: areaOf(el),
      section: sectionOf(el),
      component: customElementOf(el),
      productId,
      variantId,
      pos: positionOf(card),
    };
  };

  /* ---- clicks ---------------------------------------------------------- */

  let clicks = 0;
  let lastClick = { key: '', at: 0 };

  document.addEventListener(
    'click',
    (event) => {
      try {
        if (!event.isTrusted || clicks >= MAX_CLICKS) return;
        const target = event.target instanceof Element ? event.target : null;
        const el = target?.closest(INTERACTIVE);
        if (!el || el.closest('[data-track-ignore]')) return;
        /* A checkbox or radio reports through `change`; counting the click too would double it. */
        if (el instanceof HTMLInputElement && CHOICE_TYPES.has(el.type)) return;

        const ctx = contextOf(el);
        const href = hrefOf(el);
        const key = `${ctx.el}|${href ?? ''}|${ctx.productId ?? ''}`;
        const now = Date.now();
        if (key === lastClick.key && now - lastClick.at < DOUBLE_TAP_MS) return;
        lastClick = { key, at: now };
        clicks += 1;

        publish('forcast:click', {
          ...ctx,
          tag: el.tagName.toLowerCase(),
          text: labelOf(el),
          href,
        });
      } catch {
        /* never let analytics break a click */
      }
    },
    true
  );

  /* ---- control changes ------------------------------------------------- */

  let changes = 0;

  document.addEventListener(
    'change',
    (event) => {
      try {
        if (changes >= MAX_CHANGES) return;
        const el = event.target;
        const isSelect = el instanceof HTMLSelectElement;
        if (!isSelect && !(el instanceof HTMLInputElement)) return;
        const type = isSelect ? 'select' : el.type;
        if (!CHOICE_TYPES.has(type) || el.closest('[data-track-ignore]')) return;
        changes += 1;

        const name = controlName(el) || el.id || '';
        const choice = type === 'checkbox' || type === 'radio';
        publish('forcast:change', {
          ...contextOf(el),
          name: name || null,
          type,
          value: collapse(el.value).slice(0, 64) || null,
          checked: choice ? el.checked : null,
        });
      } catch {
        /* best effort */
      }
    },
    true
  );

  /* ---- section views --------------------------------------------------- */

  if (SECTION_VIEWS && 'IntersectionObserver' in window) {
    try {
      let sections = 0;
      let firstPass = true;
      const wrappers = Array.from(document.querySelectorAll('.shopify-section'));
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            if (sections >= MAX_SECTIONS) continue;
            sections += 1;
            publish('forcast:section_view', {
              section: sectionNameOf(entry.target),
              area: areaOf(entry.target),
              pos: wrappers.indexOf(entry.target) + 1,
              seen: firstPass ? 'load' : 'scroll',
            });
          }
          firstPass = false;
        },
        /* The middle 60% of the viewport: a section counts once the shopper has it in front of them,
           not when its first pixel peeks in at the bottom. */
        { rootMargin: '-20% 0px -20% 0px', threshold: 0 }
      );
      for (const wrapper of wrappers) observer.observe(wrapper);
    } catch {
      /* best effort */
    }
  }

  window.forcastInteractions = { version: 1, sectionViews: SECTION_VIEWS };
})();
